<template>

  <div class="position-relative">

    <v-overlay v-if="busy" :model-value="true" contained persistent class="align-center justify-center">
      <v-progress-circular indeterminate />
    </v-overlay>

    <v-defaults-provider :defaults="{VBtn: {color: null}}">

      <v-calendar
        :model-value="calendarModel"
        :events="events"
        :disabled="busy"
        :event-color="getEventColor"
        :event-height="56"
      >

        <template v-slot:event="{ event }">

          <template v-if="event">

            <!-- wrapper divs are just so we can use flexbox to center the text vertically -->
            <div class="d-flex flex-column fill-height justify-center">
              <div class="pa-2 text-center">

                <template v-if="event.type === 'summary'">
                  {{ (event as unknown as SummaryEvent).locationSpacesSold === null ? '—' : (event as unknown as SummaryEvent).locationSpacesSold }} / {{ context.locationSpacesTotal }}
                  (<template v-if="(event as unknown as SummaryEvent).locationSpacesRatio !== null">{{ percentageFormatter.format((event as unknown as SummaryEvent).locationSpacesRatio!) }}</template><template v-else>—%</template>)
                </template>

                <template v-if="event.type === 'lp'">
                  {{ event.name }}<br>
                  {{ (event as unknown as LpEvent).lpEntry.spacesSold }} / {{ (event as unknown as LpEvent).lpEntry.spacesTotal }}
                  <template v-if="(event as unknown as LpEvent).lpEntry.spacesTotal"> <!-- avoid divide-by-zero -->
                    ({{ percentageFormatter.format((event as unknown as LpEvent).lpEntry.spacesSold / (event as unknown as LpEvent).lpEntry.spacesTotal ) }})
                  </template>
                </template>

              </div>
            </div>

          </template>

        </template>

      </v-calendar>

    </v-defaults-provider>

  </div>

</template>

<script setup lang="ts">

import { computed } from 'vue'

// components
import { VCalendar } from 'vuetify/components'

import { Temporal } from '@js-temporal/polyfill'
import type { UriContext, UriLpEntry, UriState } from './types'
import { percentageFormatter } from '../../../../utils/formatters'
import VerticalRegistry from '../../../../verticals/VerticalRegistry'
import type AbstractVertical from '../../../../verticals/AbstractVertical'

interface Props {
  context: UriContext
  state: UriState
  busy: boolean
  yearMonth: Temporal.PlainYearMonth
}

const props = defineProps<Props>()

// the year and month which should be shown in the calendar
const calendarModel = computed<string>(() => {
  return props.yearMonth.toPlainDate({day: 1}).toString()
})

/**
 * Calendar events
 */

interface BaseEvent {
  type: string
  start: Date
  end: Date
  disabled: boolean
  allDay: true
}

interface SummaryEvent extends BaseEvent {
  type: 'summary'
  locationSpacesSold: null | number
  locationSpacesRatio: null | number
}

interface LpEvent extends BaseEvent {
  type: 'lp'
  lpEntry: UriLpEntry,
  name: string,
  vertical: undefined | AbstractVertical,
}

type Event = SummaryEvent | LpEvent

const events = computed<Event[]>(() => {

  const output: Event[] = []

  props.state.dates.forEach(dateEntry => {

    const jsDate = new Date(dateEntry.date.year, dateEntry.date.month - 1, dateEntry.date.day, 0, 0, 0)

    const summaryEvent: SummaryEvent = {
      type: 'summary',
      start: jsDate,
      end: jsDate,
      allDay: true,
      disabled: dateEntry.disabled,
      locationSpacesSold: dateEntry.locationSpacesSold,
      locationSpacesRatio: dateEntry.locationSpacesRatio,
    }

    output.push(summaryEvent)

    dateEntry.lps.forEach(lpEntry => {

      const lpContext = props.context.lps.find(lpContext => lpContext.lpId === lpEntry.lpId)

      const lpEvent: LpEvent = {
        type: 'lp',
        start: jsDate,
        end: jsDate,
        allDay: true,
        disabled: dateEntry.disabled,
        lpEntry: lpEntry,
        name: lpContext?.name ?? '(unknown product)',
        vertical: lpContext?.vertical ? VerticalRegistry.get(lpContext.vertical) : undefined,
      }

      output.push(lpEvent)

    })

  })

  return output

})

function getEventColor(event: Event) {
  if (event.type === 'summary') {
    return 'surface'
  }
  if (event.type === 'lp') {
    return `vertical-${event.vertical?.name}`
  }
}

</script>
